// frontend/src/pages/dashboards/ChartInUseDialog.jsx
// Phase 4 step 14a-3. Deleting a chart that a dashboard shows is refused by
// the server (409 CHART_IN_USE) with the dashboards listed; this asks whether
// to delete it anyway (the tiles stay, showing "no longer available").
// Used by the Charts page and the chart page. Focus starts on Cancel.
import React, { useRef } from 'react';
import { Dialog } from 'radix-ui';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';

/** request: { chartTitle, dashboards: [{ id, title }] } */
export function InUseContent({ request, busy = false, onCancel, onConfirm, cancelRef }) {
  const list = request.dashboards || [];
  const n = list.length;
  return (
    <>
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/10">
        <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden="true" />
      </div>
      <Dialog.Title className="mb-2 text-lg font-semibold tracking-[-0.01em]">
        {`"${request.chartTitle}" is on ${n} dashboard${n === 1 ? '' : 's'}`}
      </Dialog.Title>
      <Dialog.Description className="mb-3 text-sm text-[var(--ui-text-muted)]">
        If you delete it anyway, its tile stays on each dashboard and says the chart is no longer available until someone removes it.
      </Dialog.Description>
      <ul className="mb-5 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-xl bg-[var(--ui-inset)] p-3 text-[13px] scrollbar-thin">
        {list.map((d) => (
          <li key={d.id} className="truncate">
            <a href={`/dashboards/${d.id}`} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">
              {d.title || `Dashboard ${d.id}`}
            </a>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-end gap-3">
        <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm} disabled={busy}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          Delete anyway
        </Button>
      </div>
    </>
  );
}

/** request: null (closed), or what InUseContent takes. */
export default function ChartInUseDialog({ request, busy = false, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  return (
    <Dialog.Root open={Boolean(request)} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(e) => { e.preventDefault(); if (cancelRef.current) cancelRef.current.focus(); }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none"
        >
          {request && <InUseContent request={request} busy={busy} onCancel={onCancel} onConfirm={onConfirm} cancelRef={cancelRef} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * The delete flow both chart pages share: try the plain delete; when the
 * server lists dashboards, hand them to the dialog (via `onInUse`) and let the
 * dialog's confirm call again with force. Resolves true when deleted.
 */
export async function deleteChartWithGuard(deleteFn, chartId, { force = false, onInUse } = {}) {
  try {
    await deleteFn(chartId, { force });
    return true;
  } catch (err) {
    if (!force && err && err.code === 'CHART_IN_USE' && typeof onInUse === 'function') {
      onInUse(err.dashboards || []);
      return false;
    }
    throw err;
  }
}
